<template>
  <div data-page-name="author">
    <div class="wrapper">
      <AppBreadcrumbs
        :items="[PATHS.BLOG, fullname]"
        overwrite
        accent="light"
      />

      <h1>{{ $t('Posts_by') }} {{ fullname }}</h1>

      <Cards.MediaSpokesPeople v-bind="toMediaMediaSpokesPeopleProps(author)" />

      <ul class="cards">
        <li v-for="preview of previews" :key="preview.id">
          <Cards.Blog
            v-bind="convertArticleToCardsBlogProps(preview)"
            orientation="vertical"
          />
        </li>
      </ul>

      <AppPagination
        v-if="Number(meta.lastPage) > 1"
        v-model="page"
        :total-items="meta.total"
        :items-per-page="meta.perPage"
        :max-pages-shown="3"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { createError } from '#app';

// components
import AppBreadcrumbs from '~/components/redesign/AppBreadcrumbs.vue';
import AppPagination from '~/components/redesign/AppPagination.vue';
import Cards from '~/components/redesign/Cards';

// constants
import { STATUS_CODES, PATHS } from '~/constants';

// utils
import get from 'lodash.get';
import { convertArticleToCardsBlogProps } from '~/utils';

// types
import type { CardsMediaSpokesPeopleProps } from '~/components/redesign/Cards/MediaSpokesPeople.vue';

const router = useRouter();
const { searchParams } = useRequestURL();
const {
  params: { slug },
} = useRoute();

const page = ref(Number(searchParams.get('page')) || 1);

watch(page, async value => {
  await router.push({ query: { page: String(value) } });
  scrollToTop();
});

const { data: author } = await useAsyncGql({
  operation: 'GetAuthor',
  variables: {
    slug: slug as string,
  },
  options: {
    transform: response => get(response, 'author'),
  },
});

if (!author.value)
  throw createError({
    statusCode: STATUS_CODES.NOT_FOUND,
    message: 'Author not found',
  });

const { data } = useAsyncGql({
  operation: 'BlogArticlesByAuthor',
  variables: {
    page,
    author_id: get(author.value, 'id'),
  },
  options: {
    watch: [page],
  },
});

const previews = computed(() => get(data.value, 'blog_articles.data', []));
const meta = computed(() => get(data.value, 'blog_articles.paginatorInfo', {}));

const fullname = computed(() => get(author.value, 'data.name', ''));

const toMediaMediaSpokesPeopleProps = (
  author: unknown = {},
): CardsMediaSpokesPeopleProps => {
  return {
    avatar: get(author, 'data.data.avatar', ''),

    fullname: get(author, 'data.name', ''),
    position: get(author, 'data.data.position') ?? '',
    description: get(author, 'data.description', ''),

    linkedin: get(author, 'data.data.linkedin'),
    facebook: get(author, 'data.data.facebook'),
    reddit: get(author, 'data.data.reddit'),
    twitter: get(author, 'data.data.twitter'),
  };
};

useSeoMeta({
  title: get(author.value, 'data.seo.title'),
  ogTitle: get(author.value, 'data.seo.title'),
  description: get(author.value, 'data.seo.description'),
  ogDescription: get(author.value, 'data.seo.description'),
  ogImage: get(author.value, 'data.seo.image'),
  ogType: get(author.value, 'data.seo.og_type'),
});
</script>

<style scoped lang="scss">
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/fn';

[data-page-name='author'] {
  padding: 2rem 0 10.25rem;

  @include media.tablet {
    padding: 2rem 0 8rem;
  }

  @include media.mobile {
    padding: 2.5rem 0 5rem;
  }

  .wrapper {
    padding: 0 2rem;

    @include size.wrapper(80rem);

    @include media.mobile {
      padding: 0 1rem;
    }

    [data-component-name='AppBreadcrumbs'] {
      align-self: flex-start;
      margin-bottom: 2rem;

      @include media.mobile {
        margin-bottom: 1.5rem;
      }
    }

    [data-component-name='Cards.MediaSpokesPeople'] {
      margin-bottom: 8rem;

      @include media.mobile {
        margin-bottom: 5rem;
      }
    }

    h1 {
      @include typo.h2;

      margin-bottom: 5rem;

      @include media.tablet {
        margin-bottom: 4.5rem;
      }

      @include media.mobile {
        margin-bottom: 2.25rem;
      }
    }

    ul.cards {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      align-items: stretch;
      gap: 2rem;

      @include media.tablet {
        grid-template-columns: repeat(2, 1fr);
        gap: 1.5rem;
      }

      @include media.mobile {
        grid-template-columns: 1fr;
      }
    }

    [data-component-name='AppPagination'] {
      margin-top: 2rem;

      @include media.tablet {
        margin-top: 1.5rem;
      }
    }
  }
}
</style>
